import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { assessmentTaskSessionIdentity } from "./assessment_task_session.util";
import { LoginService } from "./login.service";

export type EvidenceRequirementsLaunchContext =
    | Readonly<{ kind: "assessment"; productLicenseId: number }>
    // The merchant hint detects stale notification scope; exact API access is authoritative.
    | Readonly<{ kind: "item"; productLicenseId: number; evidenceId: number;
        expectedMerchantId?: number; action?: "edit" | "delete" | "email" }>;

export interface EvidenceRequirementsLaunchState {
    readonly isOpen: boolean;
    readonly context: EvidenceRequirementsLaunchContext | null;
}

interface LaunchSession {
    accountId: number;
    userId: number;
    token: string;
    identity: string;
}

const CLOSED_STATE: EvidenceRequirementsLaunchState = Object.freeze({ isOpen: false, context: null });

function positiveId(value: unknown): value is number {
    return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

@Injectable({ providedIn: "root" })
export class EvidenceRequirementsLaunchService implements OnDestroy {
    private readonly state = new BehaviorSubject<EvidenceRequirementsLaunchState>(CLOSED_STATE);
    private readonly sessionSubscription: Subscription;
    private session: LaunchSession | null = null;

    constructor(private readonly login: LoginService) {
        this.sessionSubscription = login.getUserUpdates().subscribe((user) => {
            const nextSession = this.readSession(user);
            if (!this.sameSession(this.session, nextSession)) {
                this.session = nextSession;
                this.close();
            }
        });
    }

    watchState(): Observable<EvidenceRequirementsLaunchState> {
        return this.state.asObservable();
    }

    getSnapshot(): EvidenceRequirementsLaunchState {
        return this.state.value;
    }

    openAssessment(productLicenseId: number): void {
        if (!positiveId(productLicenseId)) throw new Error("A valid assessment ID is required.");
        this.open(Object.freeze({ kind: "assessment" as const, productLicenseId }));
    }

    openItem(productLicenseId: number, evidenceId: number, expectedMerchantId?: number, action?: "edit" | "delete" | "email"): void {
        if (!positiveId(productLicenseId) || !positiveId(evidenceId) ||
            (expectedMerchantId !== undefined && !positiveId(expectedMerchantId)) ||
            (action !== undefined && action !== "edit" && action !== "delete" && action !== "email")) {
            throw new Error("Valid assessment, evidence, and merchant IDs are required.");
        }
        this.open(Object.freeze({ kind: "item" as const, productLicenseId, evidenceId,
            expectedMerchantId, ...(action ? { action } : {}) }));
    }

    isCurrentContext(context: EvidenceRequirementsLaunchContext): boolean {
        return this.state.value.isOpen && this.state.value.context === context && this.sessionIsCurrent();
    }

    close(): void {
        if (this.state.value.isOpen) this.state.next(CLOSED_STATE);
    }

    ngOnDestroy(): void {
        this.sessionSubscription.unsubscribe();
        this.state.complete();
    }

    private open(context: EvidenceRequirementsLaunchContext): void {
        if (!this.sessionIsCurrent()) {
            this.close();
            return;
        }
        this.state.next(Object.freeze({ isOpen: true, context }));
    }

    private readSession(user: unknown): LaunchSession | null {
        const identity = assessmentTaskSessionIdentity(user);
        const accountId = this.login.getCurrentAccountId();
        const userId = this.login.getCurrentUserId();
        const token = this.login.getToken();
        return identity && positiveId(accountId) && positiveId(userId) && token
            ? { accountId, userId, token, identity }
            : null;
    }

    private sessionIsCurrent(): boolean {
        const session = this.session;
        return !!session && session.accountId === this.login.getCurrentAccountId() &&
            session.userId === this.login.getCurrentUserId() && session.token === this.login.getToken() &&
            session.identity === assessmentTaskSessionIdentity(this.login.getUserSnapshot());
    }

    private sameSession(left: LaunchSession | null, right: LaunchSession | null): boolean {
        return left === right || !!left && !!right && left.accountId === right.accountId &&
            left.userId === right.userId && left.token === right.token && left.identity === right.identity;
    }
}
